import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import AdjustIcon from '@mui/icons-material/Adjust';
import EventIcon from '@mui/icons-material/Event';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { tokens } from '../../../../global/theme/tokens';
import DateLabel from '../../../label/DateLabel';
import UserStack from '../../UserStack';
import AssignUserPopover from '../../../select/AssignUserPopover';
import ProjectAvatar from '../ProjectAvatar';
import {
  getPriorityColorMap,
  priorityLabelMap,
} from '../../../special/taskPriority';
import { ExoAiOriginChip } from '../../ExoAiOriginChip';

// how long the take over icon has to be held
const HOLD_MS = 1000;

/**
 * One task of the pool, with the project it belongs to on top.
 * Taking a task over is holding its icon: the border fills green while the
 * icon is held and the task is taken when it is full.
 * manage swaps the hold for a small popper that assigns any employee.
 */
const PoolTaskCard = ({
  task,
  manage,
  users = [],
  isMember,
  isAssigning,
  onTakeOver,
  onToggleUser,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const project = task.project;
  const assignees = task.users || [];
  const priorityColor = getPriorityColorMap(colors)[task.priority];

  // ---- hold to take over ----
  const [isHolding, setIsHolding] = useState(false);
  const holdTimer = useRef(null);

  // the timer must not fire into an unmounted card
  useEffect(() => () => clearTimeout(holdTimer.current), []);

  function startHold(e) {
    if (manage || isMember || isAssigning) return;
    // a second pointer (multi touch) must not start a second timer
    if (holdTimer.current) return;

    // keep the pointer even when it slips off the small icon
    e.currentTarget.setPointerCapture?.(e.pointerId);

    setIsHolding(true);
    holdTimer.current = setTimeout(() => {
      holdTimer.current = null;
      setIsHolding(false);
      onTakeOver(task);
    }, HOLD_MS);
  }

  function cancelHold() {
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
    setIsHolding(false);
  }

  // ---- assign popper (manage) ----
  const [assignAnchor, setAssignAnchor] = useState(null);

  const actionButton = manage ? (
    <Tooltip title={t('Assign employee')}>
      <IconButton
        size="small"
        onClick={(e) => setAssignAnchor(e.currentTarget)}
        sx={{
          border: `1px solid ${colors.text}25`,
          borderRadius: '8px',
        }}
      >
        {isAssigning ? (
          <span className="loader" style={{ width: '15px', height: '15px' }} />
        ) : (
          <PersonAddAltIcon sx={{ fontSize: '17px' }} />
        )}
      </IconButton>
    </Tooltip>
  ) : (
    !isMember && (
      // the span keeps the tooltip working while the button is disabled
      <Tooltip title={t('Hold to take over')}>
        <span>
          <IconButton
            size="small"
            disabled={isAssigning}
            onPointerDown={startHold}
            onPointerUp={cancelHold}
            onPointerCancel={cancelHold}
            onLostPointerCapture={cancelHold}
            // a long press on touch must not open the context menu
            onContextMenu={(e) => e.preventDefault()}
            sx={{
              border: `1px solid ${
                isHolding ? colors.success : `${colors.text}25`
              }`,
              borderRadius: '8px',
              color: isHolding ? colors.success : 'inherit',
              touchAction: 'none',
              transition: 'border-color 200ms, color 200ms',
            }}
          >
            {isAssigning ? (
              <span
                className="loader"
                style={{ width: '15px', height: '15px' }}
              />
            ) : (
              <PersonAddAltIcon sx={{ fontSize: '17px' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    )
  );

  return (
    // no card around a task - the list separates the rows with a thin line
    <Box className="relative overflow-hidden shrink-0">
      {/* the frame that fills green while the take over icon is held - the
          row clips the outer half of the stroke, leaving a 3px ring */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }}
      >
        <rect
          width="100%"
          height="100%"
          rx="8"
          fill="none"
          stroke={colors.success}
          strokeWidth="6"
          pathLength="100"
          strokeDasharray="100"
          strokeDashoffset={isHolding ? 0 : 100}
          style={{
            transition: isHolding
              ? `stroke-dashoffset ${HOLD_MS}ms linear`
              : 'stroke-dashoffset 150ms ease-out',
          }}
        />
      </svg>

      {/* priority indicator, the same stripe a task card carries */}
      <Box
        className="absolute left-0 top-0 h-full"
        sx={{ width: '4px', backgroundColor: priorityColor }}
      />

      <Box className="flex items-center gap-2 pl-3 pr-2 py-1.5">
        <Box className="flex flex-col gap-0.5 grow min-w-0">
          {project && (
            <Box
              className="flex items-center gap-1.5 min-w-0 cursor-pointer self-start max-w-full"
              onClick={() => navigate(`/projects/management/${project.id}`)}
              title={project.name}
              sx={{ ':hover': { textDecoration: 'underline' } }}
            >
              <ProjectAvatar project={project} size="16px" />
              {project.nr && (
                <Typography
                  fontSize="10px"
                  fontWeight={600}
                  className="monospace opacity-70 shrink-0"
                >
                  {project.nr}
                </Typography>
              )}
              <Typography fontSize="10px" className="opacity-60 min-w-0" noWrap>
                {project.name}
              </Typography>
            </Box>
          )}

          <Typography
            fontSize="13px"
            fontWeight={600}
            sx={{ wordBreak: 'break-word', lineHeight: 1.25 }}
          >
            <ExoAiOriginChip record={task} compact className="mr-1.5" />
            {task.name}
          </Typography>

          {/* the meta wraps onto a second line on a narrow column */}
          <Box className="flex items-center gap-x-2.5 gap-y-0.5 flex-wrap min-w-0">
            <Box
              className="flex items-center gap-0.5 shrink-0"
              sx={{ color: priorityColor }}
            >
              <KeyboardDoubleArrowUpIcon
                sx={{
                  fontSize: '13px',
                  // low priority points down, the way the task sidebar shows it
                  transform: task.priority === 1 ? 'rotate(180deg)' : 'none',
                }}
              />
              <Typography fontSize="10px">
                {t(priorityLabelMap[task.priority] || 'None')}
              </Typography>
            </Box>

            {task.effortScore != null && (
              <Box className="flex items-center gap-1 opacity-70 shrink-0">
                <AdjustIcon sx={{ fontSize: '12px' }} />
                <Typography fontSize="10px" className="monospace">
                  {`${task.effortScore} ${t('Points')}`}
                </Typography>
              </Box>
            )}

            {task.deadline && (
              <Box className="flex items-center gap-1 opacity-70 shrink-0">
                <EventIcon sx={{ fontSize: '12px' }} />
                <Typography fontSize="10px">
                  <DateLabel date={task.deadline} />
                </Typography>
              </Box>
            )}

            {Boolean(assignees.length) && (
              <Box
                className="shrink-0"
                sx={{
                  '& .MuiAvatar-root': {
                    width: '20px !important',
                    height: '20px !important',
                    fontSize: '9px',
                  },
                }}
              >
                <UserStack users={assignees} max={3} />
              </Box>
            )}
          </Box>
        </Box>

        <Box className="shrink-0 self-center">{actionButton}</Box>
      </Box>

      {manage && (
        <AssignUserPopover
          anchorEl={assignAnchor}
          onClose={() => setAssignAnchor(null)}
          users={users}
          assignedIds={assignees.map((user) => user.id)}
          isBusy={isAssigning}
          onToggleUser={(user) => onToggleUser(task, user)}
        />
      )}
    </Box>
  );
};

export default PoolTaskCard;
